import { useCallback, useEffect, useState } from "react";

import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { GoGraph } from "react-icons/go";
import { PiGear } from "react-icons/pi";
import { useLocalStorage } from "usehooks-ts";

import { ArrowBackIcon, ArrowForwardIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  chakra,
  ComponentWithAs,
  Fade,
  Flex,
  IconButton,
  IconProps,
  List,
  Text,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";

import SelectWorkspace from "@/components/SelectWorkspace";
import { Option } from "@/components/SelectWorkspace/SelectWorkspace";
import Logo from "@/components/shared/Logo";
import { ReleaseLogoIconFilled } from "@/components/shared/ReleaseIcons";
import SidebarListItem from "@/components/shared/SidebarListItem";
import SpacedustEphemeralBadge from "@/components/SpacedustEphemeralBadge";
import { ModeNavMenuProps } from "@/constants/layoutModes";
import { NavMenuItem } from "@/constants/navMenus";
import { COLLAPSED_NAV_MENU_WIDTH, NAV_MENU_WIDTH } from "@/constants/sizes";
import { useCurrentUser } from "@/providers/useCurrentUser";
import { useWorkspace } from "@/providers/useWorkspace";

const MotionDiv = chakra(motion.div);
const GraphIcon = chakra(GoGraph);

export function WorkspaceSidebar({ selectedItem, topNavMenuItems, bottomNavMenuItems }: ModeNavMenuProps) {
  const router = useRouter();
  const [collapsed, setCollapsed] = useLocalStorage<boolean>("collapsed", false);
  const { isOpen: isCollapsing, onToggle: onCollapsingToggle } = useDisclosure();
  const [showCollapse, setShowCollapse] = useState<boolean>(false);
  const allowCollapse = useBreakpointValue({ base: false, xl: true });

  // Get the workspaceId from the currentWorkspace, instead of relying on query params because
  // we might be on a page that doesn't include [workspaceId]
  const { currentWorkspace, setWorkspaceId, isSandbox } = useWorkspace();
  const workspaceId = currentWorkspace?.databaseId;
  const expired = currentWorkspace?.expiredPlan;
  const expiring = currentWorkspace?.expiringPlan;

  const handleToggle = () => {
    if (!collapsed) setCollapsed(true);
    onCollapsingToggle();
  };

  useEffect(() => {
    if (!allowCollapse && collapsed) {
      setCollapsed(false);
    }
  }, [collapsed, setCollapsed, allowCollapse]);

  const buildListItem = ({ label, icon, slug, pathname, external, onClick, ...rest }: NavMenuItem) => {
    const query = pathname.includes("[workspaceId]")
      ? {
          workspaceId,
        }
      : {};

    return (
      <SidebarListItem
        key={label}
        collapsed={collapsed}
        external={external}
        href={pathname.match(new RegExp("#|https?://")) ? pathname : { pathname: pathname, query: query }}
        icon={icon}
        isCurrent={slug === selectedItem}
        label={label}
        onClick={onClick}
        {...rest}
      />
    );
  };

  const onChangeWorkspace = useCallback(
    (next: Option) => {
      if (next.value !== workspaceId) {
        setWorkspaceId(next.value);

        if (!router.pathname.includes("[workspaceId]")) return;

        router.push({
          pathname: router.pathname.replace(/^\/workspaces\/\[workspaceId](\/[^[]*).*/, (a, b) => {
            return `/workspaces/[workspaceId]${b}`;
          }),
          query: {
            workspaceId: next.value,
          },
        });
      }
    },
    [router, workspaceId, setWorkspaceId]
  );
  const { currentUser, isEmployee } = useCurrentUser();
  const updatedBottomNavMenu = [...bottomNavMenuItems];

  if (isEmployee) {
    updatedBottomNavMenu.push({
      label: "Admin",
      icon: PiGear as ComponentWithAs<"svg", IconProps>,
      slug: "admin",
      external: true,
      pathname: `${process.env.NEXT_PUBLIC_BACKEND_BASE_URL}/admin/accounts/${workspaceId}`,
    });
  }

  const upgradeButton = buildListItem({
    icon: ReleaseLogoIconFilled,
    label: "Upgrade your plan",
    slug: "subscription",
    pathname: "/workspaces/[workspaceId]/settings/subscription",
    bg: "background.darkest",
  });

  const billingDashboard = buildListItem({
    icon: GraphIcon,
    label: "Billing Dashboard",
    slug: "dashboard",
    pathname: "/workspaces/[workspaceId]/customer/dashboard",
  });

  const { featureFlags } = currentUser || {};
  const billingDashboardEnabled = featureFlags?.find(flag => flag.key === "billing_dashboard")?.enabled;

  const workspaceFeatureFlags = currentWorkspace?.featureFlags;
  const agentsEnabled = workspaceFeatureFlags?.find(flag => flag.key === "managed_agents")?.enabled;

  const filteredTopNavMenuItems = topNavMenuItems?.filter(item => item.slug !== "agents" || agentsEnabled);

  const isEphemeral =
    String(process.env.NEXT_PUBLIC_SPACEDUST_EPHEMERAL_ENVIRONMENT).toLowerCase() === "true";

  return (
    <MotionDiv
      animate={{ width: isCollapsing ? COLLAPSED_NAV_MENU_WIDTH : NAV_MENU_WIDTH }}
      h="full"
      initial={collapsed}
      transition={{ ease: "easeInOut" }}
      onAnimationComplete={(definition: { width: string }) => {
        if (definition.width === NAV_MENU_WIDTH) {
          setCollapsed(false);
        }
      }}
    >
      <Box
        h="full"
        position="relative"
        onMouseOut={() => setShowCollapse(false)}
        onMouseOver={() => setShowCollapse(true)}
      >
        {allowCollapse && (
          <Fade
            in={showCollapse || collapsed}
            style={{
              position: "absolute",
              zIndex: 150,
              right: collapsed ? 20 : 12,
              top: collapsed ? 60 : 12,
            }}
            transition={{ exit: { delay: 0.25 } }}
          >
            <IconButton
              aria-label="collapse-sidebar"
              bgColor="background.darkest"
              icon={collapsed ? <ArrowForwardIcon /> : <ArrowBackIcon />}
              size="xs"
              variant="highlight"
              onClick={handleToggle}
            />
          </Fade>
        )}
        <Flex bg="background.darker" direction="column" h="full" position="relative" rowGap={4}>
          <Box h={20} ml={5} mt={5} w={collapsed ? "auto" : 180}>
            <Logo collapsed={collapsed || isCollapsing} />
          </Box>
          <Box px={3} w="full">
            <SelectWorkspace collapsed={collapsed} onChange={onChangeWorkspace} />
          </Box>
          <Box flex={1} overflow="visible auto" p={3} tabIndex={0}>
            <Flex bg="background.darker" direction="column" h="full" justify="space-between">
              <Box mb={8}>
                <List aria-label="primary navigation" as="nav" role="navigation" variant="navMenu">
                  {billingDashboardEnabled ? billingDashboard : null}
                  {filteredTopNavMenuItems && filteredTopNavMenuItems.map(buildListItem)}
                </List>
              </Box>
              {isEphemeral && (
                <Flex alignItems="center" direction="row" justify="center">
                  <SpacedustEphemeralBadge />
                </Flex>
              )}
              <Box>
                <List aria-label="secondary navigation" as="nav" role="navigation" variant="navMenu">
                  {isSandbox && (
                    <>
                      {(expiring || expired) && (
                        <Badge
                          alignItems="center"
                          colorScheme="brick"
                          columnGap={2}
                          display="flex"
                          mb={4}
                          ml="auto"
                          mr="auto"
                          px={2}
                          py={1}
                          variant="solid"
                          w="fit-content"
                        >
                          <WarningTwoIcon />
                          <Text>Sandbox mode {expired ? "expired" : "expiring"}</Text>
                          <WarningTwoIcon />
                        </Badge>
                      )}
                      {upgradeButton}
                    </>
                  )}
                  {updatedBottomNavMenu && updatedBottomNavMenu.map(buildListItem)}
                </List>
              </Box>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </MotionDiv>
  );
}

export default WorkspaceSidebar;
